import { GetterTree } from "vuex";
import {
  IOrganizationState,
  TravelerProfileSummary,
} from "@/core/interfaces/organizationState.interface";
import {
  IOrganization,
  ISaasEliteOrganization,
  OperationsUser,
} from "@/core/interfaces/organization.interface";
import { IOrganizationDetail } from "@/core/interfaces/organizationDetail.interface";
import {
  IWebsiteConfig,
  IWebsiteContent,
} from "@/core/interfaces/website.interface";
import { IWhatsappMember } from "@/core/interfaces/whatsappMember.interface";
import { IPassenger } from "@/core/interfaces/passenger.interface";
import { IOrganizationStats } from "@/core/interfaces/organizationStatistics.interface";
import { IOrganizationProductivity } from "@/core/interfaces/organizationProductivity.interface";
import { CurrencyConversion } from "@/core/types/index";
import { ISalesSector } from "@/core/interfaces/salesProductivity.interface";
import { StoreState } from "@/store/type";
import {
  PricingCreationForm,
  PricingItem,
} from "@/core/interfaces/managePricing.interface";
import {
  OrganizationPassengerSetupForm,
  OrganizationPassengerSetupFormV2,
} from "../models/OrganizationPassengerSetupForm.reponse";
import { IEmployeeGrade } from "@/core/interfaces/employeeGrade.interface";
import { ICostCenter } from "@/core/interfaces/costCenter.interface";
import { IDepartment } from "@/core/interfaces/department.interface";
import { IPaginationMeta } from "@/core/interfaces/state/paginationMeta.interface";
import { IAgentDetails } from "@/core/interfaces/agent.interface";

const getters: GetterTree<IOrganizationState, StoreState> = {
  organizations: (state): Array<IOrganization> => state.organizations,
  organization: (state): IOrganizationDetail | null => state.organization,
  isAddingAgent: (state): boolean => state.isAddingAgent,
  isFetchingOrganizations: (state): boolean => state.isFetchingOrganizations,
  isDeletingAgentFromOrganization: (state): boolean =>
    state.isDeletingAgentFromOrganization,
  isFetchingOrganization: (state): boolean => state.isFetchingOrganization,
  isCreatingOrganization: (state): boolean => state.isCreatingOrganization,
  isUpdatingOrganization: (state): boolean => state.isUpdatingOrganization,
  isUpdateWebsiteLoading: (state): boolean =>
    state.isUpdateWebsiteConfigurationLoading,
  organizationEmployeeGrades: (state): IEmployeeGrade[] | null =>
    state.organizationEmployeeGrades,
  organizationCostCenters: (state): ICostCenter[] | null =>
    state.organizationCostCenters,
  organizationDepartments: (state): IDepartment[] | null =>
    state.organizationDepartments,
  isFetchingOrganizationEmployeeGrades: (state): boolean =>
    state.isFetchingOrganizationEmployeeGrades,
  isFetchingOrganizationCostCenters: (state): boolean =>
    state.isFetchingOrganizationCostCenters,
  isFetchingOrganizationDepartments: (state): boolean =>
    state.isFetchingOrganizationDepartments,
  isWebsiteConfigLoading: (state): boolean => state.isWebsiteConfigLoading,
  isWebsiteContentLoading: (state): boolean =>
    state.isUpdateWebsiteContentLoading,
  websiteConfig: (state): IWebsiteConfig | null => state.websiteConfig,
  websiteContent: (state): IWebsiteContent | null => state.websiteContent,
  isWhatsappMembersLoading: (state): boolean => state.isWhatsappMembersLoading,
  isWhatsappActionLoading: (state): boolean => state.whatsappActionLoading,
  whatsappMembers: (state): IWhatsappMember[] => state.whatsappMembers,
  whatsappMembersCount: (state): number => state.whatsappMembersCount,
  whatsappMembersNext: (state): string | null => state.whatsappMembersNext,
  whatsappMembersPrevious: (state): string | null =>
    state.whatsappMembersPrevious,
  whatsappMembersCurrentPage: (state): number =>
    state.whatsappMembersCurrentPage,
  whatsappMembersPageSize: (state): number => state.whatsappMembersPageSize,
  organizationPassengers: (state): IPassenger[] | null => state.passengers,
  isFetchingOrganizationPassengers: (state): boolean =>
    state.isFetchingPassengers,
  travelerProfiles: (state): TravelerProfileSummary[] => state.travelerProfiles,
  travelerProfilesPaginationMeta: (state): IPaginationMeta | null =>
    state.travelerProfilesPaginationMeta,
  isFetchingTravelerProfiles: (state): boolean =>
    state.isFetchingTravelerProfiles,
  organizationUsers: (state): IAgentDetails[] => state.organizationUsers,
  isSearchingOrganizationUsers: (state): boolean =>
    state.isSearchingOrganizationUsers,
  isFetchingPassengerSetupForm: (state): boolean =>
    state.isFetchingPassengerSetupForm,
  passengerActionLoading: (state): boolean => state.passengerActionLoading,
  isGenerateOTPLoading: (state): boolean => state.isGenerateOTPLoading,
  isOrganizationStatsLoading: (state): boolean =>
    state.isOrganizationStatsLoading,
  isOrganizationProductivityLoading: (state): boolean =>
    state.isOrganizationProductivityLoading,
  organizationStats: (state): IOrganizationStats | null =>
    state.organizationStats,
  organizationProductivity: (state): IOrganizationProductivity | null =>
    state.organizationProductivity,
  isCurrenciesLoading: (state): boolean => state.isCurrenciesLoading,
  currencies: (state): CurrencyConversion[] => state.currencies,
  curreny: (state): string[] | null => state.curreny,
  organizationSectors: (state): ISalesSector[] | null => state.sectors,
  piaProviders: (state): string[] | null => state.pia_provider,
  operationsUsers: (state): OperationsUser[] | [] => {
    return state.operations_users;
  },
  organizationPassengerSetupForm: (
    state,
  ): OrganizationPassengerSetupForm | null =>
    state.organizationPassengerSetupForm,
  organizationPassengerSetupFormV2: (
    state,
  ): OrganizationPassengerSetupFormV2 | null =>
    state.organizationPassengerSetupFormV2,
  referenceOptions: (state: IOrganizationState) => state.referenceOptions,
  isDocumentUploadLoading: (state): boolean => state.isDocumentUploadLoading,
  saasEliteOrganization: (state): ISaasEliteOrganization[] =>
    state.saasEliteOrganization,
  isEliteOrganizationStatusUpdating: (state): boolean =>
    state.isEliteOrganizationStatusUpdating,
  errorOrgnatizationNotFound: (state): boolean =>
    state.errorOrganizationNotFound,
  airblueProviders: (state): string[] | null => state.airblue_providers,
  PIAProviders: (state): string[] | null => state.pia_providers,
  isUpdateFinancialProfileProvidersLoading: (state): boolean =>
    state.isUpdateFinancialProfileProvidersLoading,
  pricingFormField: (state): PricingCreationForm | null =>
    state.pricingFormDetails,
  isFetchPricingFormLoading: (state): boolean =>
    state.isFetchPricingFormLoading,
  pricingList: (state): PricingItem[] => state.pricingList,
  isFetchPricingListLoading: (state): boolean =>
    state.isFetchPricinglistLoading,
  selectedOrganizationId: (state): string | null =>
    state.selectedOrganizationId || null,

  activePassenger: (state): IPassenger | null => state.activePassenger,
  isFetchingActivePassenger: (state): boolean =>
    state.isFetchingActivePassenger,
  isUpdatingPassenger: (state): boolean => state.isUpdatingPassenger,
  isValidatingBulkUsers: (state): boolean => state.isValidatingBulkUsers,
  isImportingBulkUsers: (state): boolean => state.isImportingBulkUsers,
  bulkUserValidateResult: (state): unknown | null =>
    state.bulkUserValidateResult,
  bulkUserImportResult: (state): unknown | null => state.bulkUserImportResult,
};

export default getters;
